import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { Clinic, PersonalInfo } from '../../context/ReservationContext';

export interface NormalReservationState {
  prefecture: string;
  clinic: Clinic | null;
  dateTime: { date: string; time: string } | null;
  personalInfo: PersonalInfo;
  reservationNumber: string;
  loading: boolean;
  error: string | null;
}

type NormalReservationAction =
  | { type: 'SET_PREFECTURE'; payload: string }
  | { type: 'SET_CLINIC'; payload: Clinic | null }
  | { type: 'SET_DATE_TIME'; payload: { date: string; time: string } }
  | { type: 'SET_PERSONAL_INFO'; payload: Partial<PersonalInfo> }
  | { type: 'SET_RESERVATION_NUMBER'; payload: string }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'RESET_FORM' };

const initialState: NormalReservationState = {
  prefecture: '',
  clinic: null,
  dateTime: null,
  personalInfo: {
    lastName: '',
    firstName: '',
    lastNameKana: '',
    firstNameKana: '',
    phone: '',
    email: '',
    request: '',
  },
  reservationNumber: '',
  loading: false,
  error: null,
};

const normalReservationReducer = (
  state: NormalReservationState,
  action: NormalReservationAction,
): NormalReservationState => {
  switch (action.type) {
    case 'SET_PREFECTURE':
      return { ...state, prefecture: action.payload, clinic: null, dateTime: null };
    case 'SET_CLINIC':
      return { ...state, clinic: action.payload, dateTime: null };
    case 'SET_DATE_TIME':
      return { ...state, dateTime: action.payload };
    case 'SET_PERSONAL_INFO':
      return { ...state, personalInfo: { ...state.personalInfo, ...action.payload } };
    case 'SET_RESERVATION_NUMBER':
      return { ...state, reservationNumber: action.payload };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'RESET_FORM':
      return initialState;
    default:
      return state;
  }
};

interface NormalReservationContextType extends NormalReservationState {
  setPrefecture: (prefecture: string) => void;
  setClinic: (clinic: Clinic | null) => void;
  setDateTime: (dateTime: { date: string; time: string }) => void;
  setPersonalInfo: (info: Partial<PersonalInfo>) => void;
  setReservationNumber: (number: string) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  resetForm: () => void;
}

const NormalReservationContext = createContext<NormalReservationContextType | undefined>(undefined);

interface NormalReservationProviderProps {
  children: ReactNode;
}

export const NormalReservationProvider: React.FC<NormalReservationProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(normalReservationReducer, initialState);

  const setPrefecture = (prefecture: string) => dispatch({ type: 'SET_PREFECTURE', payload: prefecture });
  const setClinic = (clinic: Clinic | null) => dispatch({ type: 'SET_CLINIC', payload: clinic });
  const setDateTime = (dateTime: { date: string; time: string }) => dispatch({ type: 'SET_DATE_TIME', payload: dateTime });
  const setPersonalInfo = (info: Partial<PersonalInfo>) =>
    dispatch({ type: 'SET_PERSONAL_INFO', payload: info });
  const setReservationNumber = (number: string) => dispatch({ type: 'SET_RESERVATION_NUMBER', payload: number });
  const setLoading = (isLoading: boolean) => dispatch({ type: 'SET_LOADING', payload: isLoading });
  const setError = (error: string | null) => dispatch({ type: 'SET_ERROR', payload: error });
  const resetForm = () => dispatch({ type: 'RESET_FORM' });

  return (
    <NormalReservationContext.Provider
      value={{
        ...state,
        setPrefecture,
        setClinic,
        setDateTime,
        setPersonalInfo,
        setReservationNumber,
        setLoading,
        setError,
        resetForm,
      }}
    >
      {children}
    </NormalReservationContext.Provider>
  );
};

export const useNormalReservation = (): NormalReservationContextType => {
  const context = useContext(NormalReservationContext);
  if (!context) {
    throw new Error('useNormalReservation must be used within a NormalReservationProvider');
  }
  return context;
};
